import React from "react";
import {
  Badge,
  Button,
  DropdownList,
  DropdownListGroup,
  DropdownListItem,
  Grid,
  Typography,
} from "@sumprio/ui";
import { XCloseIcon } from "@sumprio/ui/icons";
import type { NotificationBannerRow } from "store/api/notificationsApi";
import { normalizeLabel } from "../Business/Notification/utils";
import {
  closeLayoutHelpDropdown,
  getDeploymentStatusBadgeColor,
  getSystemBannerDetailItems,
} from "./systemBannerUtils";

const DROPDOWN_LIST_MAX_HEIGHT = "min(320px, calc(100dvh - 112px))";

export interface LayoutSystemBannersProps {
  banners: NotificationBannerRow[];
  isLoading?: boolean;
  hasSession?: boolean;
}

function SystemBannerAccordionGroup({
  banner,
  showDivider,
  defaultExpanded,
}: {
  banner: NotificationBannerRow;
  showDivider: boolean;
  defaultExpanded: boolean;
}) {
  const title = String(banner.name ?? "").trim() || "System notification";
  const detailItems = getSystemBannerDetailItems(banner);
  const deploymentStatus = String(banner.deployment_status ?? "").trim();

  return (
    <DropdownListGroup
      headerLabel={title}
      expandable
      divider={showDivider}
      defaultExpanded={defaultExpanded}
      showCount={
        deploymentStatus ? (
          <Badge
            size="sm"
            type="pill-color"
            color={getDeploymentStatusBadgeColor(deploymentStatus)}
            label={normalizeLabel(deploymentStatus)}
          />
        ) : undefined
      }
    >
      {detailItems.map((item) => (
        <DropdownListItem
          key={`${banner.banner_id}-${item.label}`}
          label={item.label}
          shortcut={item.value}
          state="disabled"
        />
      ))}
    </DropdownListGroup>
  );
}

function SystemBannerDropdownHeader() {
  return (
    <Grid
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      paddingX="md"
      paddingTop="md"
      paddingBottom="xs"
    >
      <Typography variant="body" bodySize="sm" bodyWeight="semibold">
        All Notifications
      </Typography>
      <Button
        variant="brand-tertiaryGrey"
        size="xs"
        icon={XCloseIcon}
        iconOnly
        aria-label="Close notifications"
        onClick={closeLayoutHelpDropdown}
      />
    </Grid>
  );
}

function SystemBannerDropdownList({ children }: { children: React.ReactNode }) {
  return (
    <DropdownList paddingY="sm" listMaxHeight={DROPDOWN_LIST_MAX_HEIGHT} stableAccordionWidth>
      <SystemBannerDropdownHeader />
      {children}
    </DropdownList>
  );
}

export default function LayoutSystemBanners({
  banners,
  isLoading = false,
  hasSession = true,
}: LayoutSystemBannersProps) {
  if (isLoading && banners.length === 0) {
    return (
      <SystemBannerDropdownList>
        <DropdownListItem
          label={hasSession ? "Loading notifications..." : "Starting session..."}
          state="disabled"
        />
      </SystemBannerDropdownList>
    );
  }

  if (banners.length === 0) {
    return (
      <SystemBannerDropdownList>
        <DropdownListItem label="No active notifications." state="disabled" />
      </SystemBannerDropdownList>
    );
  }

  return (
    <SystemBannerDropdownList>
      {banners.map((banner, index) => (
        <SystemBannerAccordionGroup
          key={banner.banner_id}
          banner={banner}
          showDivider={index > 0}
          defaultExpanded={index === 0}
        />
      ))}
    </SystemBannerDropdownList>
  );
}
